import { createContext, useContext, useEffect, useRef } from 'react';

/**
 * Signals that the subtree is rendered INSIDE the desktop DetailPanel.
 *
 * Sheets/overlays that are normally `position: fixed` to the viewport (and
 * portal to document.body) read this to instead render constrained to the
 * panel: portal into `container` and switch `fixed` → `absolute` so they fill
 * the panel and can be closed without closing the panel itself.
 *
 * React context flows through Radix Portals (they preserve React tree
 * position), so a portaled SheetContent still sees this even though its DOM
 * node lives elsewhere — which is exactly why we also pass `container`.
 */
export interface PanelModeValue {
  inPanel: boolean;
  /** The panel's positioned body element — portal target for in-panel sheets. */
  container: HTMLElement | null;
  /**
   * Register/clear a back handler for an in-panel sub-step overlay
   * (e.g. participants, payment). While a handler is set, the DetailPanel
   * chrome replaces the breadcrumb / more / close controls with a single
   * back arrow that invokes it — closing the sub-step and returning to the
   * underlying view without closing the whole panel. Pass null to clear.
   */
  setOverlayBack: (handler: (() => void) | null) => void;
}

export const PanelModeContext = createContext<PanelModeValue>({
  inPanel: false,
  container: null,
  setOverlayBack: () => {},
});

export function useInPanel(): PanelModeValue {
  return useContext(PanelModeContext);
}

/**
 * While `active` and inside a panel, register `onBack` as the DetailPanel
 * chrome's back handler (shows a back arrow that closes this sub-step).
 *
 * `onBack` is kept in a ref so callers can pass an inline arrow without
 * causing the registration to churn (which would otherwise re-fire every
 * render and could loop, since registering triggers a panel re-render).
 */
export function usePanelOverlay(active: boolean, onBack: () => void): void {
  const { inPanel, setOverlayBack } = useContext(PanelModeContext);
  const cb = useRef(onBack);
  cb.current = onBack;
  useEffect(() => {
    if (!inPanel || !active) return;
    setOverlayBack(() => cb.current());
    return () => setOverlayBack(null);
  }, [inPanel, active, setOverlayBack]);
}
